/**
 * Role-based access control utilities
 */

// Define user roles hierarchy
export const USER_ROLES = {
  SUPER_ADMIN: 'super_admin',
  COMPANY_ADMIN: 'company_admin',
  HR_ADMIN: 'hr_admin',
  EMPLOYEE: 'employee'
};

// Define permissions for each role
export const ROLE_PERMISSIONS = {
  [USER_ROLES.SUPER_ADMIN]: [
    'dashboard', 'notifications', 'employees', 'master', 'tax_master', 'salary_structure',
    'attendance', 'leave', 'admin_leave', 'holidays', 'assets', 'performance',
    'recruitment', 'reports', 'settings', 'branch_settings'
  ],
  [USER_ROLES.COMPANY_ADMIN]: [
    'dashboard', 'analytics', 'notifications', 'employees', 'master', 'tax_master', 'salary_structure',
    'attendance', 'leave', 'admin_leave', 'holidays', 'assets', 'performance',
    'recruitment', 'reports', 'settings', 'branch_settings'
  ],
  [USER_ROLES.HR_ADMIN]: [
    'dashboard', 'analytics', 'notifications', 'employees', 'master', 'salary_structure',
    'attendance', 'leave', 'admin_leave', 'holidays', 'assets', 'performance',
    'recruitment', 'reports'
  ],
  [USER_ROLES.EMPLOYEE]: [
    'dashboard', 'notifications', 'attendance', 'leave'
  ]
};

/**
 * Check if a user has permission to access a specific feature
 * @param {string} userRole - The user's role
 * @param {string} permission - The permission to check
 * @returns {boolean} - Whether the user has the permission
 */
export const hasPermission = (userRole, permission) => {
  if (!userRole || !permission) return false;

  const permissions = ROLE_PERMISSIONS[userRole];
  return permissions ? permissions.includes(permission) : false;
};

/**
 * Check if a user has any of the specified roles
 * @param {string} userRole - The user's role
 * @param {string[]} allowedRoles - Array of allowed roles
 * @returns {boolean} - Whether the user has any of the allowed roles
 */
export const hasAnyRole = (userRole, allowedRoles) => {
  if (!userRole || !allowedRoles || !Array.isArray(allowedRoles)) return false;
  return allowedRoles.includes(userRole);
};

/**
 * Check if a user has all of the specified roles
 * @param {string} userRole - The user's role
 * @param {string[]} requiredRoles - Array of required roles
 * @returns {boolean} - Whether the user has all required roles
 */
export const hasAllRoles = (userRole, requiredRoles) => {
  if (!userRole || !requiredRoles || !Array.isArray(requiredRoles)) return false;
  return requiredRoles.every(role => role === userRole);
};

/**
 * Get the role hierarchy level (higher number = more permissions)
 * @param {string} userRole - The user's role
 * @returns {number} - The hierarchy level
 */
export const getRoleLevel = (userRole) => {
  switch (userRole) {
    case USER_ROLES.SUPER_ADMIN:
      return 4;
    case USER_ROLES.COMPANY_ADMIN:
      return 3;
    case USER_ROLES.HR_ADMIN:
      return 2;
    case USER_ROLES.EMPLOYEE:
      return 1;
    default:
      return 0;
  }
};

/**
 * Check if a user can access admin features
 * @param {string} userRole - The user's role
 * @returns {boolean} - Whether the user can access admin features
 */
export const isAdmin = (userRole) => {
  return hasAnyRole(userRole, [USER_ROLES.SUPER_ADMIN, USER_ROLES.COMPANY_ADMIN, USER_ROLES.HR_ADMIN]);
};

/**
 * Get user role display name
 * @param {string} userRole - The user's role
 * @returns {string} - The display name for the role
 */
export const getRoleDisplayName = (userRole) => {
  switch (userRole) {
    case USER_ROLES.SUPER_ADMIN:
      return 'Super Admin';
    case USER_ROLES.COMPANY_ADMIN:
      return 'Company Admin';
    case USER_ROLES.HR_ADMIN:
      return 'HR Admin';
    case USER_ROLES.EMPLOYEE:
      return 'Employee';
    default:
      return 'User';
  }
};
